import { motion, AnimatePresence } from 'framer-motion';
import { playBossSound } from '@/utils/sounds';
import { useEffect, useState } from 'react';
import gachiGif from '@/assets/gachimuchi.gif';

interface GachiRewardProps {
  visible: boolean;
  onDismiss: () => void;
}

export function GachiReward({ visible, onDismiss }: GachiRewardProps) {
  const [canDismiss, setCanDismiss] = useState(false);

  useEffect(() => {
    if (visible) {
      playBossSound();
      setCanDismiss(false);
      const timer = setTimeout(() => {
        setCanDismiss(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [visible]);

  const handleClick = () => {
    if (canDismiss) {
      onDismiss();
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className={`fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm ${canDismiss ? 'cursor-pointer' : 'cursor-default'}`}
          onClick={handleClick}
          role="dialog"
          aria-modal="true"
          aria-label="Gachimuchi reward - click to dismiss"
        >
          {/* Radial glow background */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1.5, opacity: 0.4 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 1.5, ease: 'easeOut' }}
            className="absolute w-96 h-96 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(138, 43, 226, 0.6) 0%, transparent 70%)',
            }}
          />
          
          {/* The GIF reward */}
          <motion.div
            initial={{ 
              scale: 0, 
              rotate: 0,
              opacity: 0 
            }}
            animate={{ 
              scale: [0, 0.3, 0.6, 1.2, 1],
              rotate: [0, 180, 360, 720, 720],
              opacity: 1 
            }}
            exit={{ 
              scale: 0, 
              rotate: 1080,
              opacity: 0 
            }}
            transition={{ 
              duration: 2,
              ease: [0.34, 1.56, 0.64, 1],
              times: [0, 0.3, 0.5, 0.8, 1]
            }}
            className="relative"
          >
            {/* Golden border glow */}
            <motion.div
              animate={{
                boxShadow: [
                  '0 0 20px rgba(255, 215, 0, 0.5), 0 0 40px rgba(255, 215, 0, 0.3)',
                  '0 0 40px rgba(255, 215, 0, 0.8), 0 0 80px rgba(255, 215, 0, 0.5)',
                  '0 0 20px rgba(255, 215, 0, 0.5), 0 0 40px rgba(255, 215, 0, 0.3)',
                ],
              }}
              transition={{ duration: 2, repeat: Infinity }}
              className="rounded-full overflow-hidden"
            >
              <img
                src={gachiGif}
                alt="Gachimuchi Reward"
                className="w-64 h-64 sm:w-80 sm:h-80 object-cover rounded-full border-4 border-yellow-400"
              />
            </motion.div>
            
            {/* Sparkles around */}
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ 
                  scale: [0, 1, 0],
                  opacity: [0, 1, 0],
                }}
                transition={{
                  duration: 1.5,
                  delay: 1.5 + i * 0.1,
                  repeat: Infinity,
                  repeatDelay: 1,
                }}
                className="absolute w-4 h-4 text-yellow-300"
                style={{
                  top: '50%',
                  left: '50%',
                  transform: `translate(-50%, -50%) rotate(${i * 45}deg) translateY(-150px)`,
                }}
              >
                ✦
              </motion.div>
            ))}
          </motion.div>
          
          {/* Click to dismiss text */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: canDismiss ? 1 : 0.5, y: 0 }}
            transition={{ delay: canDismiss ? 0 : 2.5 }}
            className="absolute bottom-8 text-white/60 text-sm"
          >
            {canDismiss ? 'Кликни, чтобы закрыть' : 'Подожди...'}
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
